import { useState } from "react"
import { Button } from "@/components/Primitives/Button"
import { Share2, Check } from "lucide-react"
import { useClipboard } from "use-clipboard-copy"
import { toast } from "sonner"
import { ShareModal } from "@/components/Primitives/ShareModal"
import { useConfig } from "@/hooks/useConfig"
import { cn } from "@/lib/utils/utils"

interface ShareButtonProps {
  url: string
  title: string
  text: string
  className?: string
}

function shouldShowNativeShare() {
  if (typeof navigator === "undefined" || !("share" in navigator)) {
    return false
  }

  // Only use native share on mobile devices
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0

  return isMobile || isTouchDevice
}

export function ShareButton({
  url,
  title,
  text,
  className = "",
}: ShareButtonProps) {
  const [isSharing, setIsSharing] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  useClipboard()
  const { t } = useConfig()

  const handleShare = async () => {
    setIsSharing(true)

    try {
      if (shouldShowNativeShare()) {
        await navigator.share({
          title,
          text,
          url,
        })
        toast.success(t("config_share_success"), {
          style: {
            backgroundColor: "#4ade80",
            color: "white",
            border: "1px solid #4ade80",
          },
        })
      } else {
        setIsModalOpen(true)
      }
    } catch (error) {
      console.error("Error sharing:", error)
      setIsModalOpen(true)
    } finally {
      setIsSharing(false)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  return (
    <>
      <Button
        onClick={handleShare}
        disabled={isSharing}
        className={cn("flex items-center gap-2 w-full md:w-fit", className)}
        variant="outline"
      >
        {isSharing ? (
          <Check className="h-4 w-4" />
        ) : (
          <Share2 className="h-4 w-4" />
        )}
        {isSharing ? t("config_share_sharing") : t("config_share_button")}
      </Button>

      <ShareModal
        isOpen={isModalOpen}
        onClose={closeModal}
        url={url}
        title={title}
        text={text}
      />
    </>
  )
}
